/**
 * The demo's sign-up cards, in the moment (owner, 2026-10-13: "no CTA to sign up" for Ask, "the give it to someone page also never
 * CTAs", and "Sending a Report should say what it WOULD do when complete and say sign up now"). DEMO ONLY: a real business never
 * sees any of this (every entry point asks `config.demo` or `isDemoSample` first).
 *
 * One card, one primary button ("Start your 25 days free", COPY-STANDARD section on the one ask), one plain line under it
 * ("25 days free. Cancel anytime."), one open door ("Not now"). The card says what THIS action does in the visitor's own account.
 * B = MAT: it rides on the tap the visitor just made (the action is the motivation), one tap away from sign-up. Where an action
 * sends something (a report), the lines are what the real app does, read from the same tables the server reads (core incidents.ts
 * INCIDENT_TYPES, handoff.ts), never invented: `reportWould` lists them, and test/demo-cta.test.tsx maps every line to the flag it
 * comes from.
 *
 * The Ask cap (2 sample questions a session, counted in this browser) lives in lib/demoTour.ts with the other per-session counts.
 */
import { useSyncExternalStore } from "react";
import { INCIDENT_TYPES, REFERRAL_MONTHS, TRIAL_DAYS, incidentInfo, monthsWord, type IncidentType } from "@logwave/core";
import { ApiError } from "../api.ts";
import { demoSignupUrl } from "./demoAsk.ts";

export type CtaId = "ask" | "prep" | "invite" | "report" | "team" | "message" | "billing";

/** The one button, the one plain line. */
export const CTA_BUTTON = `Start your ${TRIAL_DAYS} days free`;
export const CTA_TRIAL_LINE = `${TRIAL_DAYS} days free. Cancel anytime.`;
export const CTA_NOT_NOW = "Not now";
/** The quiet button in the demo's header on every screen. */
export const HEADER_CTA = `Use it free for ${TRIAL_DAYS} days`;

export interface Cta { id: CtaId; title: string; body: string }
export const CTAS: Record<CtaId, Cta> = {
  ask: { id: "ask", title: "Ask anything about your kitchen, answered with the Food Code section.", body: "You have asked your 2 sample questions." },
  prep: { id: "prep", title: "Set up a place for a friend's restaurant", body: "Walk it with photos, then hand it over." },
  invite: { id: "invite", title: "Invite a business", body: `You get ${monthsWord(REFERRAL_MONTHS.business)} when it pays for its first full month, once you have your own account.` },
  report: { id: "report", title: "Sending a report works in your own account", body: "In your account, this report would:" },
  team: { id: "team", title: "Invite your team", body: "Send each person an invite by text or email, from your own account." },
  message: { id: "message", title: "Message the CleanCleaver team", body: "Send us a question from your own account." },
  billing: { id: "billing", title: "Start your free days", body: "Add a card for your own place, change your plan, or see your receipts." },
};

/** The sign-up link of a card: the tour's own destination (their kitchen's setup, then the card step) with the card's source. */
export const ctaUrl = (id: CtaId, host?: string) => demoSignupUrl("gate", host, { state: "UT", go: "trial", src: `demo-${id}` });

/* ─────────────── what a report would do (the real app's behaviour, one line each) ─────────────── */

type IncidentInfo = (typeof INCIDENT_TYPES)[number];
export interface ReportLine {
  /** The behaviour it comes from: a flag of the report's type, or the route's rule. */
  basis: "handoff" | "record" | "private" | "external" | "dedupe";
  when: (t: IncidentInfo) => boolean;
  text: (t: IncidentInfo) => string;
}
/**
 * Read from the real code: POST /orgs/:orgId/incidents (services/api/src/routes/work.ts) saves the report open with its time, room and
 * reporter ("record"), puts it on the list of the person in charge on shift or else the managers and owners ("handoff": core handoff.ts;
 * no push, so we never say "right away" or "push"), keeps no words from a sick report ("private": core withoutPrivateText), marks a
 * county-health or pest-company report ("external": Report.tsx says who to call), and adds a repeat within `dedupeHours` as "still
 * there" ("dedupe": core findDuplicate). A small mess cleaned up now notifies nobody (core mess.ts), so "mess" has no handoff line.
 */
export const REPORT_LINES: readonly ReportLine[] = [
  { basis: "handoff", when: (t) => t.notify.length > 0, text: () => "Go on the list of the person in charge on your shift, or your managers and owners when no one is on." },
  { basis: "record", when: (t) => t.ask !== "private", text: () => "Be saved with the time, the room and who sent it, and stay open until a manager marks it fixed." },
  { basis: "private", when: (t) => t.ask === "private", text: () => "Be saved as a sick report that keeps no words from you, only that it happened." },
  { basis: "external", when: (t) => t.externalReport === "county_health", text: () => "Be marked for a call to the county health department the same day." },
  { basis: "external", when: (t) => t.externalReport === "pest_company", text: () => "Be marked for a call to your pest control company." },
  { basis: "dedupe", when: (t) => t.dedupeHours > 0 && t.ask !== "person", text: (t) => `Add a second report of the same thing within ${t.dedupeHours} hours as "still there", not a duplicate.` },
];
export function reportWould(type: IncidentType | string): string[] {
  const t = INCIDENT_TYPES.find((x) => x.type === type) ?? incidentInfo("other");
  return REPORT_LINES.filter((l) => l.when(t)).map((l) => l.text(t));
}

/* ─────────────── the card showing (one at a time) ─────────────── */

export interface CtaShown { id: CtaId; /** A report's type, for its lines. */ type?: string }
let shown: CtaShown | null = null;
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
export function announceCta(id: CtaId, type?: string) { shown = { id, ...(type ? { type } : {}) }; for (const l of listeners) l(); }
export function closeCta() { shown = null; for (const l of listeners) l(); }
export function useCta(): CtaShown | null { return useSyncExternalStore(subscribe, () => shown, () => null); }

/** The refusal an account-only call answers with (the card is already showing). */
export class DemoCtaError extends ApiError {
  constructor(public cta: CtaId) { super(403, "demo_cta", "This works in your own account."); this.name = "DemoCtaError"; }
}

/**
 * The api calls whose main action needs a real account. In the demo each opens its card instead of running (lib/demoTour.ts
 * withDemoLock). The Report's Send is on the screen itself (it knows the type); a check's automatic report is not here, so a
 * fridge reading that is too warm never opens a card mid-check.
 */
export const ACCOUNT_METHODS: Readonly<Record<string, CtaId>> = {
  createPrep: "prep", prepQueue: "prep", prepPay: "prep", prepCompose: "prep", prepSent: "prep",
  createReferralInvite: "invite",
  invite: "team", resendInvite: "team", remind: "team",
  contactSupport: "message", replySupport: "message",
  billingCheckout: "billing", billingPortal: "billing", cancelPlan: "billing",
};
